'use client';

import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import * as Sentry from "@sentry/nextjs";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function AppError({ error, reset }: ErrorProps) {
  const pathname = usePathname();
  
  useEffect(() => {
    // Log the error to an error reporting service
    console.error(`Application error on ${pathname}:`, error);
    console.log('Error digest:', error.digest);
    console.error('Fatal application error:', error);
    console.log('Error digest:', error.digest);
    Sentry.captureException(error);
    // Here you could integrate with an error tracking service like Sentry
    // Example Sentry integration:
    // if (typeof window !== 'undefined' && window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     tags: { path: pathname },
    //     level: 'error',
    //   });
    // }
  }, [error, pathname]);

  return (
    <div className="container mx-auto flex flex-col items-center justify-center min-h-[70vh] px-4">
      <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-md w-full text-center shadow-lg">
        <div className="flex justify-center mb-4">
          <div className="bg-red-100 p-3 rounded-full">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          Oops! Something went wrong
        </h2>
        
        <p className="text-gray-600 mb-6">
          We encountered an unexpected error. Our team has been automatically notified, and we're working to fix the issue.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            variant="outline" 
            className="border-gray-300 hover:bg-gray-100" 
            onClick={reset}
          >
            Try again
          </Button>
          
          <Button 
            variant="default"
            className="bg-primary hover:bg-primary/90"
            asChild
          >
            <Link href="/dashboard">Return to dashboard</Link>
          </Button>
        </div>
        
        {error.digest && (
          <p className="mt-6 text-xs text-gray-400">
            Error ID: {error.digest}
          </p>
        )}
      </div>
    </div>
  );
} 